/** Brand colors surfaced to the banner. Hex strings (e.g. "#012060"). */
export interface ConsentColors {
  primary: string;
  secondary: string;
  text: string;
  accent: string;
  /** Highlighted "accept all" button (kept separate so it can stand out). */
  accept: string;
}

/**
 * Which consent categories the banner offers. `necessary` is always present and
 * read-only, so it is not listed here.
 *  - analytics    -> analytics_storage
 *  - ads          -> ad_storage, ad_user_data, ad_personalization
 *  - calltracking -> functionality_storage, personalization_storage (the "functional" category)
 */
export interface ConsentCategories {
  analytics: boolean;
  ads: boolean;
  calltracking: boolean;
}

/**
 * The single contract shared by every consumer (WordPress plugin, Astro template).
 * Adapters build this from their own config source and pass it to both
 * `headSnippet()` and `init()`.
 */
export interface ConsentLinks {
  /** Privacy policy URL (Datenschutzerklärung). Linked from the banner. */
  privacy?: string;
  /** Imprint URL (Impressum). Linked from the banner. */
  imprint?: string;
}

export type ConsentCategoryName = 'analytics' | 'ads' | 'functional' | 'necessary';

/**
 * An individual service shown under its category in the preferences modal (e.g.
 * "Google Analytics 4"). The `key` is the Borlabs service key, so per-service
 * consent flows straight into the GTM `hasConsent('<key>')` lookup.
 */
export interface ConsentService {
  key: string;
  name: string;
  category: ConsentCategoryName;
  /** Per-service detail card (eRecht24-style). Per-site fields come from Borlabs,
   *  purposes/technologies/data from the curated catalog (merged at render time). */
  description?: string;
  /** Processing company incl. address, e.g. "Google Ireland Limited, Dublin…". */
  company?: string;
  /** Provider privacy policy URL. */
  privacyUrl?: string;
  /** Provider cookie policy URL. */
  cookieUrl?: string;
  /** Data-processing purposes (e.g. Werbung, Analyse). */
  purposes?: string[];
  /** Technologies used (e.g. Cookies, Tracking Pixel). */
  technologies?: string[];
  /** Collected data points (e.g. IP-Adresse, Geräteinformationen). */
  data?: string[];
}

export interface ConsentConfig {
  /** GTM container id, e.g. "GTM-XXXXXX". When absent/invalid, GTM is not loaded. */
  gtmId?: string;
  /** Banner UI language. */
  lang: 'de' | 'en';
  colors: ConsentColors;
  categories: ConsentCategories;
  /** Legal page links shown in the banner footer. */
  links?: ConsentLinks;
  /** Block embedded third-party content (YouTube, Maps, …) until consent. */
  contentBlocker?: boolean;
  /** Show a floating "cookie settings" button so visitors can reopen preferences. */
  settingsButton?: boolean;
  /** Individual services listed per category (migrated from Borlabs). */
  services?: ConsentService[];
  /** Show an admin-only transparency panel listing what was allowed vs blocked. */
  debug?: boolean;
}

const DEFAULT_COLORS: ConsentColors = {
  primary: '#012060',
  secondary: '#0C6FB3',
  text: '#18181B',
  accent: '#D26A34',
  accept: '#012060',
};

/** Fill in defaults and normalize a partial/untrusted config from an adapter. */
export function normalizeConfig(input: Partial<ConsentConfig> | undefined | null): ConsentConfig {
  const cfg = input ?? {};
  const gtmId = typeof cfg.gtmId === 'string' && /^GTM-[A-Z0-9]+$/i.test(cfg.gtmId.trim())
    ? cfg.gtmId.trim()
    : undefined;
  const links: ConsentLinks = {};
  if (typeof cfg.links?.privacy === 'string' && cfg.links.privacy) links.privacy = cfg.links.privacy;
  if (typeof cfg.links?.imprint === 'string' && cfg.links.imprint) links.imprint = cfg.links.imprint;

  const validCats = ['analytics', 'ads', 'functional', 'necessary'];
  const strArr = (v: unknown): string[] | undefined =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x) : undefined;
  const services: ConsentService[] = Array.isArray(cfg.services)
    ? cfg.services
        .filter((s): s is ConsentService => !!s && typeof s.key === 'string' && !!s.key && typeof s.name === 'string')
        .map((s) => {
          const out: ConsentService = {
            key: s.key,
            name: s.name,
            category: (validCats.includes(s.category) ? s.category : 'functional') as ConsentCategoryName,
          };
          if (typeof s.description === 'string' && s.description) out.description = s.description;
          if (typeof s.company === 'string' && s.company) out.company = s.company;
          if (typeof s.privacyUrl === 'string' && s.privacyUrl) out.privacyUrl = s.privacyUrl;
          if (typeof s.cookieUrl === 'string' && s.cookieUrl) out.cookieUrl = s.cookieUrl;
          const pu = strArr(s.purposes);
          const te = strArr(s.technologies);
          const da = strArr(s.data);
          if (pu) out.purposes = pu;
          if (te) out.technologies = te;
          if (da) out.data = da;
          return out;
        })
    : [];

  return {
    gtmId,
    lang: cfg.lang === 'en' ? 'en' : 'de',
    colors: { ...DEFAULT_COLORS, ...(cfg.colors ?? {}) },
    categories: {
      analytics: cfg.categories?.analytics ?? true,
      ads: cfg.categories?.ads ?? true,
      calltracking: cfg.categories?.calltracking ?? false,
    },
    links,
    contentBlocker: cfg.contentBlocker ?? false,
    settingsButton: cfg.settingsButton ?? true,
    services,
    debug: cfg.debug ?? false,
  };
}

/**
 * Borlabs service-key -> our category. The Borlabs GTM "Consent Variable" template
 * calls BorlabsCookie.Consents.hasConsent('<service-key>'); we answer based on our
 * categories so existing GTM containers keep working unchanged. Extend as needed.
 */
export const BORLABS_SERVICE_MAP: Record<string, 'analytics' | 'ads' | 'functional' | 'necessary'> = {
  'google-analytics': 'analytics',
  'google-ads': 'ads',
  'google-tag-manager': 'necessary',
  'meta-pixel-for-wordpress': 'ads',
  'facebook-pixel': 'ads',
  facebook: 'ads',
  'microsoft-ads': 'ads',
  'linkedin-insight': 'ads',
  // Content-blocker / external-media service keys (Borlabs uses short keys, e.g. "maps").
  maps: 'functional',
  'google-maps': 'functional',
  youtube: 'functional',
  vimeo: 'functional',
  instagram: 'functional',
  'google-fonts': 'necessary',
  // reCAPTCHA loads a Google script directly (not via GTM) -> gate it.
  recaptcha: 'functional',
};
